import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { AppliedLoanStatus } from '@qred/shared-enums';
import React from 'react';
import {
  CampaignType,
  CardPurposeType,
  ClientType,
  CountryCode,
  FlowTypes,
  GlobalErrorType,
  InvoiceStatuses,
  StepStatus,
} from './enums';
import { OwnerType } from './pages/LoanOffer/components/Ownership/CompanyOwnersCard';
import { ApiStatus, InvoiceState, Language } from './store/types/sharedTypes';
import { ModalState } from './contexts/modals.context';
import { CardInvoiceDay, Product, Signer } from './types/types';

export interface IntlState {
  language: Language;
  languageIsSet: boolean;
  market: CountryCode;
  marketIsSet: boolean;
}
export interface ModalsState {
  modals: ModalState[];
  current: ModalState | null;
}

export interface HelpPopupState {
  isOpen: boolean;
}

export interface CustomGlobalError {
  title: string;
  description?: string;
  buttonText?: string;
}

export interface GlobalErrorState {
  hasError: boolean;
  errorType: GlobalErrorType;
  showBanner: boolean;
  showCardOfferBanner: boolean;
  isTokenSet: boolean;
  customGlobalError?: CustomGlobalError;
  globalErrorPersistent?: boolean;
}

export interface ApiStatusPayload {
  companyLookupData?: ApiStatus;
  application?: ApiStatus;
  clients?: ApiStatus;
  offer?: ApiStatus;
  patch?: ApiStatus;
  post?: ApiStatus;
  guarantors?: ApiStatus;
  fetchKycQuestions?: ApiStatus;
  postKycAnswers?: ApiStatus;
}

export interface ApiObjectData {
  [key: string]: any;
}

export type ApiListData = Array<ApiObjectData>;

export type LoanStatus =
  | 'Open_Active'
  | 'Open_Late'
  | 'Open_In_Collection'
  | 'Closed_Credit_Limit_Change';

export interface User {
  firstName: string;
  lastName: string;
  personalNumber: string;
  phone: string;
  email: string;
  city: string;
  zip: string;
  street: string;
  country: string;
  language: Language | null;
}

export interface CompanyBase {
  id: number;
  name: string;
  number: string;
}

export interface Company extends CompanyBase {
  city: string;
  email: string;
  loanAmount: number;
  loanAmountLeft: number;
  loanId: number;
  loanStatus: string;
  phone: string;
  street: string;
  zip: string;
  loan?: Loan;
  previousLoans?: PreviousLoans[];
  application?: Application;
}

export interface LoanBase {
  id: number;
  companyId: number;
  orgNumber: string;
  companyName: string;
  total: number;
  remaining: number;
  loanTerms: number;
  fees: number;
  interestPercent: number;
  status: LoanStatus;
  penalty: number;
  granter: 'qred' | 'nordiska';
}

export interface Loan extends LoanBase {
  nextPaymentObj: Payment | null;
  overduePayments: Array<Payment> | null;
  details: Array<Payment>;
}

export interface PreviousLoans {
  amountPaid: number;
  status: string;
  id: number;
  amountTotal: number;
  amountLeft: number;
  term: number;
  companyId: number;
  invoices: Array<Invoice>;
  startDate: string;
  endDate: string;
  closedDate: string;
  monthsToPayout: number | null;
}

export interface Invoice {
  amount: number;
  amount_open: number;
  date_paid?: string | null;
  end_date: string;
  line_no: number;
  month_index: number;
  monthly_fee: number;
  r_contract_line_id: number;
  start_date: string;
  term_date: string;
}
export interface TerminatedContract {
  amountToPay: string;
  clientId: number;
  contractId: number;
  earlyRepaymentAmount: string;
  granterBank: string;
  ocr: string;
  status: string;
  terminatedDate: string;
  sentTime: string;
  showTermination: boolean;
}

export interface Offer {
  loanPurposeManual?: string;
  maxAllowedAmount: number;
  interest: number;
  payOutDate?: Date;
  companyName: string;
  companyNumber: string;
  representativeName: string;
  maxAllowedTerm: number;
  bindingTerm: number;
  invoiceEmail: string;
  purposeOfLoan: string;
  accountNumber: string;
  applicationId: number;
  language: string;
  clientId: number;
  personalNumber: string;
  dateOfBirth: string;
  clientName: string;
  signers: Array<Signer>;
  isLunarPartner?: boolean;
  campaignType: CampaignType | null;
  campaignPrincipalFreeMonths: number;
  campaignInterestRateDiscount?: number;
  campaignDiscount?: number;
  currentAmountLeft: number;
  amount: number;
  amountOriginal: number;
  source: string;
  uuid: string;
}

export interface OfferUserData {
  owners: Array<LoanOwner>;
  yourOwnShares: string;
  toPayout: number;
  amount: number;
  accountNumber: string;
  purposeOfLoan: string;
  loanPurposeManual: string;
  ownerType: string;
  loanPeriod: number;
  monthlyFee: number;
  monthlyAmortization: number;
  invoiceEmail: string;
  signers: Array<Signer>;
  offerIsAgreed: boolean;
  personalLiabilityIsAgreed: boolean;
  regularMailForInvoices: boolean;
  applicationId: number;
  clientId: number;
  personalNumber: string;
  clientName: string;
  isPolicyAgreed: boolean;
}

export interface CardOffer {
  applicationUuid: string;
  creditLimit: number | null;
  hasAcceptedTerms: boolean;
  interest: number;
  invoiceAddress: string;
  invoiceEmail: string;
  shippingAddress: string | null;
  offeredCreditLimit: number;
  isPaperInvoiceChosen: boolean;
  additionalSigners: Array<CardSigner>;
  beneficialOwners: Array<ILegacyOnboardingOwner>;
  companyName: string;
  companyId: number;
  companyType: string;
  isPersonalGuaranteeRequired: boolean;
  cardOfferAlreadySigned: boolean;
  applicationReasons: Array<CardOfferPurpose>;
  ownerStructure: OwnerType;
  applicantOwnerShare: string | undefined;
  hasAcceptedPersonalGuarantee: boolean;
  invoiceDay: CardInvoiceDay;
  companyNo: string | null;
  applicant: any | null; // TODO: fix type
}

export interface CardSigner {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  signerId: string;
}

export interface CardBeneficialOwner {
  fullName: string;
  ownerShare: number;
  personalNumber: string;
  id?: string;
  isAddedByUser?: boolean;
}

export interface CardOfferPurpose {
  reason: CardPurposeType;
  reasonText: string | null;
}

export type CardOfferUserData = CardOffer;

export interface ShippingDetails {
  cardHolderFullName: {
    firstName: string;
    lastName: string;
  };
  companyName: string;
  companyType: string;
  mobilePhone: string;
  email: string;
  addresses: Array<{
    street: string;
    city: string;
    country: string;
    zipCode: string;
  }>;
  cardAlreadyCreated: boolean;
}
export interface ShippingDetailsUserData extends ShippingDetails {
  address: {
    street: string;
    city: string;
    country: string;
    zipCode: string;
  };
  cardHolderSelectedFullName: {
    firstName: string;
    lastName: string;
  };
  companyNameOnCard: string;
  shouldHaveCompanyNameOnCard: boolean;
}

export interface CardOfferSigner {
  cardApplicationUuid: string;
  hasAcceptedPersonalGuarantee: boolean;
  // isPersonalGuaranteeRequired: boolean;
  hasAcceptedTerms: boolean;
  interest: number;
  offeredCreditLimit: number;
  companyName: string;
  signerUuid: string;
}

export interface Payment {
  loanId: string;
  index: number;
  datePaid: Date | null;
  dueDate: Date;
  amortization: number;
  interest: number;
  totalMonthAmount: number;
  penalty: number;
  invoiceLineId: number;
  amortizationPaid: number | null;
}

export interface LoanOwner {
  name: string;
  personalNumber: string;
  percentage: string;
  id: string;
}

export interface ApplicationBase {
  amount: number;
  applicationDate: Date | string;
  loanPurpose: string | null;
  applicationId: number;
  contractId: number | null;
  signers: Signer[];
  status: AppliedLoanStatus;
  term: number;
}

export interface ILegacyOnboardingOwner {
  fullName: string;
  personalNumber?: string | null;
  dateOfBirth?: Date | null;
  percentage: string;
  id: string;
  isAddedByUser?: boolean;
  ownerShare?: number;
}

export interface MenuItem {
  label: string;
  onClick: () => void;
  icon: IconProp;
  active?: boolean;
  hide?: boolean;
  dataCy?: string;
}

export type Application = ApplicationBase & CompanyBase;

export interface StepperHeaderProps {
  title?: string;
  description?: string;
}

export interface StepConfigItem {
  stepContent: React.FC[];
  header?: StepperHeaderProps;
  customHeader?: React.FC;
  customFooter?: React.FC;
  name: keyof IStepName;
}

export interface IApplicationIfClientNotExist {
  application_date: string;
  application_uuid: string;
  status: AppliedLoanStatus;
}

export interface SingleInvoice {
  activatedById: string;
  activatedDate: string;
  articleRows: [];
  balances: Balance;
  clientId: string;
  clientName: string;
  collectionBatchId: string | null;
  createdBy: string;
  createdDate: string;
  currency: Currency;
  dates: {
    invoiceDate: string;
    expireDate: string;
    latestFinancialDate: string;
  };
  debitInvoiceNumber: string | null;
  debtor: Debtor;
  distributionCode: string;
  document: Document;
  documentTemplateCode: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  fees: {};
  id: string;
  invoiceNumber: string;
  isRecourse: boolean;
  ourOrderNumber: string | null;
  ourReference: string;
  partPayment: PartPayment;
  paymentReference: string;
  productCode: string;
  refinancingExternalId: string;
  refund: boolean;
  remark: string;
  reminderProfileCode: string;
  reverseCharge: boolean;
  serviceProviderCode: string;
  statuses: InvoiceStatus;
  terms: { payment: string; delivery: string | null };
  activityLogs: [];
  amount: string;
  assignees: Array<string>;
  client: any;
  clientLogo: string;
  clientOrgNumber: string;
  comments: string | null;
  created: string;
  debtorOrgNumber: string;
  documents: null;
  externalId: string;
  invoiceAttachment: string;
  invoiceCurrency: string;
  invoicePreview: string;
  manualCreditDecision: boolean;
  partInvoices: any[];
  partPaymentPlan: Instalment[];
  status: string;
  termsAndConditionsAccepted: boolean;
  totals: Total;
  type: string;
  useRecourse: boolean;
  vats: [];
  warnings: string;
  yourContactPerson: string | null;
  yourOrderNumber: string;
  yourReference: string;
}

export interface Instalment {
  index: number;
  plannedDate: string;
  annuityAmount: number;
  annuityInstalment: number;
  annuityInterest: number;
}

export interface Debtor {
  FMI: string | null;
  GLN: string | null;
  cellPhone: string | null;
  customNumber: string | null;
  email: string;
  id: string;
  identificationNumber: string;
  name: string;
  phone: string | null;
  type: string;
  beneficialOwners: BeneficialOwner[];
  beneficialOwnersChanged: boolean;
  orgNumber: string;
  uboApproved: boolean;
}

interface Currency {
  invoiceCurrency: string;
}

interface Balance {
  capital: number;
  capitalLocale: number;
  total: number;
  totalLocale: number;
}

interface Document {
  attachment: string;
  connectedFileCount: number;
  invoice: string;
  invoicePreview: string;
  invoiceAttachment: string;
  invoiceWithAttachments: string;
}
interface PartPayment {
  isPartPayment: boolean;
  partPaymentPlanId: string;
}

export interface InvoiceStatus {
  ledger: string | null;
  ledgerSubType: string | null;
  main: InvoiceStatuses;
  refinancing: string;
}

// Open invoices - Öppna fakturor   (unpaid now)
// Paid invoices - Betalda fakturor  (paid now)
// Archived - Arkiverade

export enum InvoiceTabValue {
  OPEN = 'Open',
  PAID = 'Paid',
  ARCHIVED = 'Archived',
}

export interface InvoiceSection {
  label: string | JSX.Element[];
  key: InvoiceTabValue;
}

interface Total {
  total: number;
  totalToPay: number;
}

export interface HubSpotConversations {
  clear: (options?: { resetWidget: boolean }) => void;
  debug: () => void;
  off: (
    event:
      | 'conversationStarted'
      | 'conversationClosed'
      | 'unreadConversationCountChanged'
      | 'contactAssociated',
    fn: () => void
  ) => void;
  on: (
    event:
      | 'conversationStarted'
      | 'conversationClosed'
      | 'unreadConversationCountChanged'
      | 'contactAssociated',
    fn: () => void
  ) => void;
  resetAndReloadWidget: () => void;
  widget: {
    close: () => void;
    load: (options?: { widgetOpen: boolean }) => void;
    open: () => void;
    refresh: (options?: { openToNewThread: boolean }) => void;
    remove: () => void;
    status: () => {
      loaded: boolean;
      pending: boolean;
    };
  };
}

export interface IOwner {
  name: string;
  personalNumber: string;
  email?: string;
  amount: string;
}

export interface OwnerApiInterface {
  name: string;
  email?: string;
  personalNo: string;
  ownerExtentCode: string;
}

export interface MatchedMediaBreakPoints {
  mobile: boolean;
  smallScreen: boolean;
  mobileLandscape: boolean;
  smallScreenPortrait: boolean;
  mediumScreen: boolean;
  bigScreen: boolean;
  portrait: boolean;
  landscape: boolean;
  tablet: boolean;
  desktop: boolean;
}

export interface UnpaidInvoice {
  id: number;
  contractLineId: number;
  contractId: number;
  generated: boolean;
  amount: number;
  monthIndex: number;
  dateAuthorized: string;
  dueDate: string;
  amortization: number;
  reference: string;
  state: InvoiceState;
  lateFee?: number;
  latePenaltyFee?: number;
  monthlyFee: number;
  total: number;
  sentDate: string;
}

export interface PaidInvoice {
  id: number;
  contractLineId: number;
  amount: number;
  paymentDate: string;
  amortization: number;
  lateFee?: number;
  latePenaltyFee?: number;
  monthlyFee: number;
  total: number;
}

export interface Invoices {
  relatedLoanId: number;
  unpaid: UnpaidInvoice[];
  paid: PaidInvoice[];
}

export interface BeneficialOwner {
  name: string;
  companyName?: string;
  personalNo: string;
  clientId?: string;
  ownsMoreThan75Precent?: boolean;
  ownPercent?: string;
  isBeneficialOwner?: boolean;
  ownerExtentCode: string;
  created?: Date;
  email?: string;
}

export interface SliderValues {
  defaultValue: number;
  min: number;
  max: number;
  step: number;
}

export interface AvailableMarkets<T> {
  se: T;
  nl: T;
  fi: T;
  dk: T;
  be: T;
  no: T;
}

export type LoanTermValueDefaults = SliderValues;

interface IAwsFields {
  key: string;
  bucket: string;
  Policy: string;
  ['X-Amz-Date']: string;
  ['X-Amz-Algorithm']: string;
  ['X-Amz-Signature']: string;
  ['X-Amz-Credential']: string;
  ['X-Amz-Security-Token']: string;
}
export interface IFileInformation {
  url: string;
  fields: IAwsFields;
}

export interface IValueDefaults {
  min: number;
  max: number;
  step: number;
}

export interface IAnalyticEventParams {
  eventName?: string;
  errorMessage?: string;
  stepNumber?: number;
  stepName?: keyof IStepName;
  stepStatus?: StepStatus;
  product?: Product;
  userId?: number;
  actionName?: string;
  stepActionNumber?: number;
  market?: CountryCode;
  language?: Language;
  clientType?: ClientType;
  flowType?: FlowTypes;
  orderId?: number;
  timeSpentOnStep?: number; // time spent in seconds
  timestamp?: string; // date in ISO string
}

export interface IEvent {
  onboarding_application_flow?: string;
  onboarding_offer_flow?: string;
}

export interface IStepName {
  user_login?: string;
  company_selector_step?: string;
  personal_information_step?: string;
  loan_purpose_step?: string;
  landing_page?: string;
  financial_information_step?: string;
  connect_to_bank_step?: string;
  connect_to_eskat_step?: string;
  guarantor_selector_step?: string;
  adjustment_step?: string;
  contract_summary_step?: string;
  bank_information_step?: string;
  billing_and_shipping_information_step?: string;
  signer_step?: string;
  card_offer_step?: string;
  ownership_step?: string;
  kyc_questionnaire_step?: string;
  additional_signers_and_guarantors_step?: string;
  thank_you_onboarding_offer_step?: string;
  card_purpose_step?: string;
}
